import {
  formatarInteiro,
  formatarPontoSerie,
  formatarPontoSerieLongo,
} from '@/features/relatorios/formato';
import { tetoDoEixo } from './escala';

export interface PontoSerie {
  periodo: string;
  total: number;
}

interface Props {
  pontos: PontoSerie[];
  granularidade: 'dia' | 'mes';
}

/** Sistema de coordenadas fixo; o SVG escala com a largura do cartão. */
const LARGURA = 720;
const ALTURA = 240;
const MARGEM = { topo: 14, direita: 10, baixo: 26, esquerda: 46 };
const LINHAS_GRADE = 4;
/** Acima disso, o eixo X vira borrão: rotula-se um a cada N. */
const MAXIMO_ROTULOS = 9;

/**
 * Série temporal em linha com área.
 *
 * A granularidade é decidida pelo backend — dia até três meses, mês acima
 * disso — e chega junto com os pontos, para que o rótulo do eixo case com o
 * que foi realmente agrupado.
 */
export function GraficoSerie({ pontos, granularidade }: Props) {
  if (pontos.length === 0) {
    return (
      <p className="text-silenciado-texto py-10 text-center text-sm">
        Nenhuma solicitação registrada no período.
      </p>
    );
  }

  const larguraUtil = LARGURA - MARGEM.esquerda - MARGEM.direita;
  const alturaUtil = ALTURA - MARGEM.topo - MARGEM.baixo;
  const teto = tetoDoEixo(Math.max(...pontos.map((ponto) => ponto.total)));

  const x = (indice: number): number =>
    pontos.length === 1
      ? MARGEM.esquerda + larguraUtil / 2
      : MARGEM.esquerda + (indice / (pontos.length - 1)) * larguraUtil;

  const y = (valor: number): number => MARGEM.topo + alturaUtil - (valor / teto) * alturaUtil;

  const coordenadas = pontos.map((ponto, indice) => ({
    ...ponto,
    cx: x(indice),
    cy: y(ponto.total),
  }));

  const linha = coordenadas.map((ponto) => `${ponto.cx},${ponto.cy}`).join(' ');
  const base = MARGEM.topo + alturaUtil;
  // Sobe até o primeiro ponto, percorre a série e volta pela linha de base.
  const area =
    `M ${x(0)},${base} L ${coordenadas.map((ponto) => `${ponto.cx},${ponto.cy}`).join(' L ')} ` +
    `L ${x(pontos.length - 1)},${base} Z`;

  const passoRotulo = Math.ceil(pontos.length / MAXIMO_ROTULOS);
  const total = pontos.reduce((soma, ponto) => soma + ponto.total, 0);
  const pico = coordenadas.reduce((maior, ponto) => (ponto.total > maior.total ? ponto : maior));

  return (
    <div>
      <svg
        viewBox={`0 0 ${LARGURA} ${ALTURA}`}
        className="h-auto w-full"
        role="img"
        aria-label={
          `Evolução por ${granularidade === 'mes' ? 'mês' : 'dia'}: ` +
          `${formatarInteiro(total)} solicitações em ${pontos.length} períodos, ` +
          `com pico de ${formatarInteiro(pico.total)} em ${formatarPontoSerieLongo(pico.periodo)}.`
        }
      >
        {/* Grade e rótulos do eixo vertical. */}
        {Array.from({ length: LINHAS_GRADE + 1 }, (_, indice) => {
          const valor = (teto / LINHAS_GRADE) * indice;
          const alturaLinha = y(valor);
          return (
            <g key={indice}>
              <line
                x1={MARGEM.esquerda}
                x2={LARGURA - MARGEM.direita}
                y1={alturaLinha}
                y2={alturaLinha}
                className="stroke-borda"
                strokeWidth={1}
              />
              <text
                x={MARGEM.esquerda - 8}
                y={alturaLinha + 4}
                textAnchor="end"
                className="fill-silenciado-texto text-[11px]"
              >
                {formatarInteiro(valor)}
              </text>
            </g>
          );
        })}

        <path d={area} className="fill-marca opacity-10" />
        {pontos.length > 1 && (
          <polyline
            points={linha}
            fill="none"
            className="stroke-marca"
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        )}

        {coordenadas.map((ponto, indice) => (
          <g key={ponto.periodo}>
            <circle cx={ponto.cx} cy={ponto.cy} r={3} className="fill-marca">
              <title>{`${formatarPontoSerieLongo(ponto.periodo)}: ${formatarInteiro(ponto.total)}`}</title>
            </circle>
            {(indice % passoRotulo === 0 || indice === pontos.length - 1) && (
              <text
                x={ponto.cx}
                y={ALTURA - 6}
                textAnchor="middle"
                className="fill-silenciado-texto text-[11px]"
              >
                {formatarPontoSerie(ponto.periodo)}
              </text>
            )}
          </g>
        ))}
      </svg>

      {/* O gráfico é imagem; os números precisam existir também como texto. */}
      <table className="apenas-leitor-tela">
        <caption>Solicitações por período</caption>
        <tbody>
          {pontos.map((ponto) => (
            <tr key={ponto.periodo}>
              <th scope="row">{formatarPontoSerieLongo(ponto.periodo)}</th>
              <td>{formatarInteiro(ponto.total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
